import React, { type ReactNode, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
/////////////////////////////////////////
import { Helmet } from 'react-helmet';
/////////

import { getLogger } from 'app-components/utils/core';

import CombinedStyleProviders from 'CombinedStyleProviders';
import ErrorBoundary from 'components/ErrorBoundary';
import PrototypeWatermark from 'components/PrototypeWatermark';
import AppRouter from 'containers/AppRouter';
/////////////////////////////////////////
import PreviewLabel from 'components/PreviewLabel';
import { useAppStore } from 'hooks/redux';
import { persistedAuthSession } from 'store/persistedAuthSession';
import { isDevSsoAttempt } from 'utils/devSso/capture';
import DevSsoGate from 'utils/devSso/login';
/////////

import { AppStoreProvider } from './AppStoreProvider';
import { BootstrapErrorBoundary } from './BootstrapErrorBoundary';
import { useOnlineStatusSync } from './useOnlineStatusSync';

const log = getLogger('startup/AppRoot');

function AppContent(): React.JSX.Element {
  useOnlineStatusSync();

  let appContent: ReactNode = <AppRouter />;
  let previewBadge: ReactNode = null;
  let loggedOutHelmet: ReactNode = null;
///////////////////////////////////////////
  const store = useAppStore();
  const previewLabel = process.env.PREVIEW_LABEL;
  if (previewLabel) previewBadge = <PreviewLabel label={previewLabel} />;
  // ContentSecurityPolicy mounts only after authentication.
  if (!persistedAuthSession.qcsId || !persistedAuthSession.datasetId) {
    loggedOutHelmet = <Helmet defaultTitle={previewLabel ? `${previewLabel}: Simplifi` : 'Simplifi'} />;
  }
  if (isDevSsoAttempt()) {
    appContent = (
      <DevSsoGate store={store}>
        <AppRouter />
      </DevSsoGate>
    );
  }
///////////

  return (
    <CombinedStyleProviders>
      <ErrorBoundary>
        <DndProvider backend={HTML5Backend} context={window}>
          {loggedOutHelmet}
          {appContent}
          <PrototypeWatermark />
          {previewBadge}
        </DndProvider>
      </ErrorBoundary>
    </CombinedStyleProviders>
  );
}

/** The app's root: startup error handling, store bootstrap, providers and the router. */
export function AppRoot(): React.JSX.Element {
  // After the loading screen's first render, not the store's: a slow cache restore is a load worth measuring. The path
  // is read here because the metrics describe the route the page loaded on, which can change before the chunk arrives.
  useEffect(() => {
    const { pathname } = window.location;
    import('utils/webVitals')
      .then(({ startWebVitals }) => startWebVitals(pathname))
      .catch((error: unknown) => log.warn('Web vitals failed to start', error));
  }, []);

  return (
    <BootstrapErrorBoundary>
      <AppStoreProvider>
        <AppContent />
      </AppStoreProvider>
    </BootstrapErrorBoundary>
  );
}
